
import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { toast } from '@/hooks/use-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<'admin' | 'waiter' | 'kitchen' | 'delivery'>;
}

// Ensure we're defining a proper React function component
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = ['admin', 'waiter', 'kitchen', 'delivery'] 
}) => {
  const location = useLocation();
  
  // Wrap the context usage in a try-catch to prevent null context errors
  try {
    const { currentUser } = useApp();

    console.log('ProtectedRoute check:', { 
      currentUser, 
      path: location.pathname,
      allowedRoles,
      isManager: currentUser?.isManager
    });

    // Se não estiver autenticado, redirecionar para login
    if (!currentUser) {
      return <Navigate to="/login" replace />;
    }

    // IMPORTANTE: Verificando explicitamente se isManager é true (para garantir que não seja undefined ou null)
    const isAdminOrManager = currentUser.role === 'admin' || currentUser.isManager === true;
    
    console.log('Verificação de permissões:', { 
      role: currentUser.role, 
      isManager: currentUser.isManager,
      isAdminOrManager
    });
    
    // Se o usuário é admin ou manager, permitir acesso a todas as páginas
    if (isAdminOrManager) {
      console.log('Usuário é admin ou gestor (isManager=true), acesso permitido a todas páginas');
      return <>{children}</>;
    }
    
    // Para outros usuários, verificar se o papel está na lista de permitidos
    if (allowedRoles.length > 0 && !allowedRoles.includes(currentUser.role)) {
      // Mostrar mensagem de acesso restrito
      setTimeout(() => {
        toast({
          title: 'Acesso restrito',
          description: 'Você não tem permissão para acessar esta página.',
          variant: 'destructive',
        });
      }, 100);
      
      // Redirecionar para rota apropriada com base na função
      let redirectPath = '';
      
      switch (currentUser.role) {
        case 'waiter':
          redirectPath = 'tables';
          break;
        case 'kitchen':
          redirectPath = 'kitchen';
          break;
        case 'delivery':
          redirectPath = 'delivery';
          break;
        default:
          redirectPath = '';
          break;
      }
      
      console.log(`Redirecionando usuário para: ${redirectPath}`);
      return <Navigate to={`/${redirectPath}`} replace />;
    }

    return <>{children}</>;
  } catch (error) {
    console.error("Error in ProtectedRoute:", error);
    return <Navigate to="/login" replace />;
  }
};

export default ProtectedRoute;
